import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import css from './css/AddStudentSurveyElementModal.scss';
import commonCss from './css/SurveyStyle.scss';
import AddStudentSurveyElementForm from './AddStudentSurveyElementForm';
import Header from 'common/Header';
import Footer from 'common/Footer';
import StyledButton from 'common/StyledButton';
import ErrorMessage from 'common/ErrorMessage';
import { isEmptyString } from 'utils/text';
import { getWord } from 'utils/tenant';
import { merge, mergeWith, isArray } from 'lodash';
import PropTypes from 'prop-types';

const initialElementDataState = {
    elementText: null,
    templateQuestion: null
};

const initialErrorState = {
    formError: null,
    fieldError: null
};

const AddStudentSurveyElementModal = ({isOpen, closeModal, currentQuestionIds, elementType, onSave, elementDataToEdit}) => {
    Modal.setAppElement(document.getElementById('main-center'));

    const [elementData, setElementData] = useState(initialElementDataState);
    //the front facing version of elementType that should be displayed
    //e.g. elementType 'HEADER' will have elementTypeDisplayName as 'Section Header'
    const [elementTypeDisplayName, setElementTypeDisplayName] = useState('');
    const [addElementDescription, setAddElementDescription] = useState('');
    const [errors, setErrors] = useState(initialErrorState);

    useEffect(() => {
        switch (elementType) {
            case 'HEADER':
                setElementTypeDisplayName('Section Header');
                setAddElementDescription('Add a section header to group questions into sections. Specify the text in the Section Header field.');
                break;
            case 'QUESTION':
                setElementTypeDisplayName('Question');
                setAddElementDescription(`Choose a question to add and specify whether or not to make this question \
                    required for this ${getWord('studentSurvey', false)}.`);
                break;
            case 'INSTRUCTION':
                setElementTypeDisplayName('Text');
                setAddElementDescription(`Instructional or other text can be added to the ${getWord('studentSurvey', false)} \
                    to provide guidance. Specify the text in the Text field.`);
                break;
        }
        setElementData(elementDataToEdit || initialElementDataState);
    }, [elementType, elementDataToEdit]);

    const resetModal = () => {
        setElementData(initialElementDataState);
        setErrors(initialErrorState);
        closeModal();
    };

    const isQuestionInUse = () => elementDataToEdit?.templateQuestion.questionId !== elementData.templateQuestion.questionId
        && currentQuestionIds.some(questionId => questionId === elementData.templateQuestion.questionId)

    const validate = () => {
        if (elementType === 'HEADER' && isEmptyString(elementData.elementText)) {
            setErrors(merge({}, errors, {fieldError: 'Provide section header text.'}));
        }
        else if (elementType === 'INSTRUCTION' && isEmptyString(elementData.elementText)) {
            setErrors(merge({}, errors, {fieldError: 'Provide instructional text.'}));
        }
        else if (elementType === 'QUESTION' && !elementData.templateQuestion?.questionId) {
            setErrors(merge({}, errors, {fieldError: 'Select a question.'}));
        }
        else if (elementType === 'QUESTION' && isQuestionInUse()) {
            setErrors(merge({}, errors, {fieldError: 'The question you tried to add is already in use.'}));
        }
        else {
            onSave(elementData);
            resetModal();
        }
    };

    const onElementUpdate = (newElementData) =>
        setElementData(mergeWith({},
            elementData,
            newElementData,
            // When the property to merge is an array, we want to replace the whole array not individual items.
            // e.g. When updating templateQuestion.questionOptions, we want to replace the whole list of
            // questionOptions rather than merge with the current list.
            (_, newValue) => isArray(newValue) ? newValue : undefined// eslint-disable-line no-undefined
        ));

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={resetModal}
            overlayClassName={css['overlay']}
            className={css['modal']}
            shouldCloseOnOverlayClick={false}
            contentLabel={`${elementDataToEdit ? 'Edit' : 'Add'} ${elementTypeDisplayName}`}
        >
            <div className={css['container']}>
                <Header closeModal={resetModal} />
                {errors.formError && <ErrorMessage message={errors.formError} />}

                <div className={css['content']}>
                    <div className={commonCss['header-third-level']}>{elementDataToEdit ? 'Edit' : 'Add'} {elementTypeDisplayName}</div>
                    <span className={commonCss['instructional-text']}>{addElementDescription}</span>
                    <AddStudentSurveyElementForm elementType={elementType}
                                                 elementTypeDisplayName={elementTypeDisplayName}
                                                 elementData={elementData}
                                                 onElementUpdate={onElementUpdate}
                                                 errors={errors}
                                                 onErrorUpdate={(error) => setErrors(merge({}, errors, error))} />
                </div>
                <Footer
                    primaryButton={<StyledButton onClick={validate}><>Save {elementTypeDisplayName}</></StyledButton>}
                    secondaryButton={<StyledButton asLink onClick={resetModal}>Cancel</StyledButton>}
                />
            </div>
        </Modal>
    );
};

AddStudentSurveyElementModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
    currentQuestionIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    elementType: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired,
    elementDataToEdit: PropTypes.shape({
        elementText: PropTypes.string,
        templateQuestion: PropTypes.shape({
            questionId: PropTypes.number.isRequired,
            questionText: PropTypes.string.isRequired,
            questionType: PropTypes.string.isRequired,
            questionOptions: PropTypes.arrayOf(PropTypes.shape({
                text: PropTypes.string.isRequired,
                position: PropTypes.number.isRequired
            })).isRequired,
            required: PropTypes.bool.isRequired
        })
    })
};

export default AddStudentSurveyElementModal;
